import React, { useState, useEffect, useContext } from 'react';
import styled from 'styled-components';
import { AuthContext } from '../contexts/AuthContext';
import DashboardHeader from '../components/Layout/DashboardHeader';
import supabase from '../config/supabaseClient';
import { calculateNextPayment } from '../utils/dateUtils';

const PageContainer = styled.div`
  min-height: 100vh;
  background: var(--background);
`;

const CalendarPageContainer = styled.div`
  padding: 24px;
  max-width: 1200px;
  margin: 0 auto;
`;

const PageHeader = styled.div`
  margin-bottom: 24px;
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const Title = styled.h1`
  font-size: 24px;
  color: var(--text-primary);
  margin: 0;
`;

const CalendarWrapper = styled.div`
  background: white;
  border-radius: 12px;
  box-shadow: var(--shadow-sm);
  padding: 24px;
`;

const CalendarContainer = styled.div`
  background: white;
  border-radius: 12px;
  padding: 20px;
  width: 100%;
`;

const CalendarHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 24px;
  padding: 0 8px;
`;

const MonthNavigation = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
`;

const MonthDisplay = styled.div`
  font-size: 20px;
  font-weight: 600;
  color: var(--text-primary);
  min-width: 200px;
  text-align: center;
`;

const NavButton = styled.button`
  background: white;
  border: 1px solid var(--border);
  border-radius: 8px;
  cursor: pointer;
  color: var(--text-primary);
  padding: 8px 16px;
  font-size: 16px;
  display: flex;
  align-items: center;
  gap: 8px;
  transition: all 0.2s ease;

  &:hover {
    background: var(--hover);
    border-color: var(--text-secondary);
  }

  svg {
    width: 16px;
    height: 16px;
  }
`;

const TodayButton = styled.button`
  background: var(--primary);
  color: white;
  border: none;
  border-radius: 8px;
  padding: 8px 16px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    opacity: 0.9;
  }
`;

const CalendarGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(7, 1fr);
  gap: 1px;
  background: var(--border);
  border: 1px solid var(--border);
  border-radius: 8px;
  overflow: hidden;
  min-height: 600px;
`;

const WeekdayHeader = styled.div`
  background: #f8f9fa;
  padding: 12px;
  text-align: center;
  font-size: 14px;
  font-weight: 500;
  color: var(--text-secondary);
`;

const DateCell = styled.div`
  position: relative;
  min-height: 100px;
  padding: 8px;
  border: 1px solid var(--border);
  background: ${props => props.isToday ? 'var(--primary-light)' : 'var(--surface)'};
  cursor: pointer;

  &:hover {
    background: var(--hover);
  }

  ${props => props.isToday && `
    .date-number {
      background: var(--primary);
      color: white;
      border-radius: 50%;
      width: 24px;
      height: 24px;
      display: flex;
      align-items: center;
      justify-content: center;
    }
  `}

  ${props => props.isEmpty && `
    background: #fafafa;
  `}
`;

const DateNumber = styled.div`
  font-size: 14px;
  margin-bottom: 4px;
  width: 24px;
  height: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const TileContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  margin-top: 4px;
`;

const TileAmount = styled.div`
  font-size: 12px;
  font-weight: 500;
  color: var(--primary);
`;

const TileCount = styled.div`
  font-size: 10px;
  color: var(--text-secondary);
`;

const TileSubscriptions = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
  max-height: 60px;
  overflow-y: auto;
`;

const SubscriptionName = styled.div`
  font-size: 12px;
  color: var(--text-primary);
  background: var(--primary-light);
  padding: 2px 6px;
  border-radius: 4px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  z-index: 1;
`;

const YearSelector = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  gap: 8px;
`;

const YearButton = styled.button`
  background: white;
  border: 1px solid var(--border);
  border-radius: 8px;
  padding: 8px 16px;
  font-size: 16px;
  color: var(--text-primary);
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 8px;
  transition: all 0.2s ease;

  &:hover {
    background: var(--hover);
    border-color: var(--text-secondary);
  }

  svg {
    width: 16px;
    height: 16px;
  }
`;

const YearDropdown = styled.div`
  position: absolute;
  top: 100%;
  left: 0;
  right: 0;
  background: white;
  border: 1px solid var(--border);
  border-radius: 8px;
  box-shadow: var(--shadow-md);
  max-height: 300px;
  overflow-y: auto;
  z-index: 10;
  margin-top: 4px;
`;

const YearOption = styled.div`
  padding: 8px 16px;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background: var(--hover);
  }

  ${props => props.selected && `
    background: var(--primary);
    color: white;

    &:hover {
      background: var(--primary);
      opacity: 0.9;
    }
  `}
`;

const loadSubscriptions = async (user, setCalendarData, setLoading) => {
  try {
    setLoading(true);
    const { data: subscriptions, error } = await supabase
      .from('subscriptions')
      .select('*')
      .eq('user_id', user.id)
      .eq('status', 'active');

    if (error) throw error;

    // Process subscriptions to add next payment dates
    const processedSubscriptions = subscriptions.map(subscription => {
      const nextPaymentDate = calculateNextPayment(subscription.start_date, subscription.cycle);
      return {
        ...subscription,
        dueDate: nextPaymentDate
      };
    });

    // Group by due date
    const grouped = processedSubscriptions.reduce((acc, subscription) => {
      const date = subscription.dueDate;
      if (!acc[date]) {
        acc[date] = [];
      }
      acc[date].push(subscription);
      return acc;
    }, {});

    console.log('Grouped subscriptions by due date:', grouped);
    setCalendarData(grouped);

  } catch (error) {
    console.error('Error loading subscriptions:', error);
  } finally {
    setLoading(false);
  }
};

const CalendarPage = () => {
  const { user } = useContext(AuthContext);
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [calendarData, setCalendarData] = useState({});
  const [selectedSubscriptions, setSelectedSubscriptions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showYearSelector, setShowYearSelector] = useState(false);

  useEffect(() => {
    if (user) {
      loadSubscriptions(user, setCalendarData, setLoading);
    }
  }, [user]);

  // Set up real-time subscription updates
  useEffect(() => {
    if (!user) return;

    const subscription = supabase
      .channel('subscription_changes')
      .on('postgres_changes', 
        {
          event: '*',
          schema: 'public',
          table: 'subscriptions',
          filter: `user_id=eq.${user.id}`
        },
        () => {
          // Reload subscriptions when any change occurs
          loadSubscriptions(user, setCalendarData, setLoading);
        }
      )
      .subscribe();

    return () => {
      subscription.unsubscribe();
    };
  }, [user]);

  const handleDateClick = (date) => {
    const formattedDate = date.toISOString().split('T')[0];
    const subscriptionsForDate = calendarData[formattedDate] || [];
    setSelectedSubscriptions(subscriptionsForDate);
    setSelectedDate(date);
  };

  const getYearOptions = () => {
    const currentYear = new Date().getFullYear();
    return Array.from({ length: 10 }, (_, i) => currentYear - 5 + i);
  };

  const renderCalendar = () => {
    const weekdays = ['SUN', 'MON', 'TUE', 'WED', 'THU', 'FRI', 'SAT'];
    const today = new Date();
    
    // Get first and last day of selected month
    const firstDay = new Date(selectedDate.getFullYear(), selectedDate.getMonth(), 1);
    const lastDay = new Date(selectedDate.getFullYear(), selectedDate.getMonth() + 1, 0);
    const daysInMonth = lastDay.getDate();
    
    const days = [];

    // Add empty cells for padding at start of month
    const startPadding = firstDay.getDay();
    for (let i = 0; i < startPadding; i++) {
      days.push({ date: null, isEmpty: true });
    }

    // Add current month's days
    for (let i = 1; i <= daysInMonth; i++) {
      const date = new Date(selectedDate.getFullYear(), selectedDate.getMonth(), i);
      days.push({ date, isEmpty: false });
    }

    // Add empty cells for padding at end of month
    const endPadding = 42 - days.length; // 42 = 6 rows * 7 days
    for (let i = 0; i < endPadding; i++) {
      days.push({ date: null, isEmpty: true });
    }

    return (
      <CalendarContainer>
        <CalendarHeader>
          <MonthNavigation>
            <NavButton onClick={() => {
              const newDate = new Date(selectedDate);
              newDate.setMonth(newDate.getMonth() - 1);
              setSelectedDate(newDate);
            }}>
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
              Previous
            </NavButton>
            <YearSelector>
              <YearButton onClick={() => setShowYearSelector(!showYearSelector)}>
                {selectedDate.getFullYear()}
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </YearButton>
              {showYearSelector && (
                <YearDropdown>
                  {getYearOptions().map(year => (
                    <YearOption
                      key={year}
                      selected={year === selectedDate.getFullYear()}
                      onClick={() => {
                        const newDate = new Date(selectedDate);
                        newDate.setFullYear(year);
                        setSelectedDate(newDate);
                        setShowYearSelector(false);
                      }}
                    >
                      {year}
                    </YearOption>
                  ))}
                </YearDropdown>
              )}
            </YearSelector>
            <MonthDisplay>
              {selectedDate.toLocaleString('default', { month: 'long' })}
            </MonthDisplay>
            <NavButton onClick={() => {
              const newDate = new Date(selectedDate);
              newDate.setMonth(newDate.getMonth() + 1);
              setSelectedDate(newDate);
            }}>
              Next
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            </NavButton>
          </MonthNavigation>
          <TodayButton onClick={() => setSelectedDate(new Date())}>
            Today
          </TodayButton>
        </CalendarHeader>

        <CalendarGrid>
          {weekdays.map(day => (
            <WeekdayHeader key={day}>{day}</WeekdayHeader>
          ))}
          {days.map(({ date, isEmpty }, index) => {
            if (isEmpty) {
              return <DateCell key={index} isEmpty />;
            }

            const formattedDate = date.toISOString().split('T')[0];
            const subscriptionsForDate = calendarData[formattedDate] || [];

            // Debug log for each cell
            console.log(`Cell date ${formattedDate}:`, subscriptionsForDate);

            return (
              <DateCell 
                key={index}
                isToday={date.toDateString() === today.toDateString()}
              >
                <DateNumber>{date.getDate()}</DateNumber>
                {subscriptionsForDate.length > 0 && (
                  <TileContent>
                    <TileSubscriptions>
                      {subscriptionsForDate.map((sub, idx) => (
                        <SubscriptionName key={idx}>
                          {sub.name || 'Unnamed Subscription'}
                        </SubscriptionName>
                      ))}
                    </TileSubscriptions>
                  </TileContent>
                )}
              </DateCell>
            );
          })}
        </CalendarGrid>
      </CalendarContainer>
    );
  };

  if (loading) {
    return (
      <PageContainer>
        <DashboardHeader />
        <CalendarPageContainer>
          <div>Loading...</div>
        </CalendarPageContainer>
      </PageContainer>
    );
  }

  return (
    <PageContainer>
      <DashboardHeader />
      <CalendarPageContainer>
        <PageHeader>
          <Title>Subscription Calendar</Title>
        </PageHeader>
        <CalendarWrapper>
          {renderCalendar()}
        </CalendarWrapper>
      </CalendarPageContainer>
    </PageContainer>
  );
};

export default CalendarPage;
